import { supabase } from '@/lib/supabase';
import { withIdempotency, type IdempotencyOutcome } from '@/lib/coreIdempotency';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 46. API / ACTION LAYER
// Zifek Core fournit la fondation de l'« Action Layer ». Une action est un
// enveloppe générique qui décrit, exécute et journalise une opération métier :
//
//   action_id · actor · tenant · resource · permission · parameters ·
//   status · result · timestamp
//
// Exemple :
//   create_appointment
//     Actor      : user_123
//     Resource   : professional_456
//     Permission : appointment.create
//     Result     : appointment_789
//
// Chaque action : contrôle de permission → exécution → journalisation
// (core_actions), avec idempotence optionnelle (spec 47).
// ─────────────────────────────────────────────────────────────

export type ActionStatus = 'pending' | 'success' | 'failed' | 'denied' | 'duplicate';

export interface CoreActionDefinition {
  actionKey: string;
  label: string;
  description: string;
  resourceType: string;
  permission: string;
  icon: string;
  /** L'action est-elle idempotente par nature ? */
  idempotent: boolean;
  /** Domaine d'idempotence (orders, reservations…) le cas échéant. */
  idempotencyScope?: string;
}

/** Catalogue des actions standard du socle (fondation de l'Action Layer). */
export const CORE_ACTIONS: CoreActionDefinition[] = [
  { actionKey: 'create_customer', label: 'Créer un client', description: 'Crée une fiche client.', resourceType: 'customer', permission: 'customers.write', icon: 'ri-user-add-line', idempotent: true, idempotencyScope: 'orders' },
  { actionKey: 'create_appointment', label: 'Créer un rendez-vous', description: 'Réserve un créneau pour un professionnel.', resourceType: 'appointment', permission: 'appointment.create', icon: 'ri-calendar-check-line', idempotent: true, idempotencyScope: 'appointments' },
  { actionKey: 'create_booking', label: 'Créer une réservation', description: 'Réserve une ressource exclusive (chambre, villa…).', resourceType: 'booking', permission: 'products.write', icon: 'ri-hotel-line', idempotent: true, idempotencyScope: 'reservations' },
  { actionKey: 'create_order', label: 'Créer une commande', description: 'Enregistre une commande.', resourceType: 'order', permission: 'orders.write', icon: 'ri-shopping-bag-3-line', idempotent: true, idempotencyScope: 'orders' },
  { actionKey: 'create_invoice', label: 'Créer une facture', description: 'Émet une facture.', resourceType: 'invoice', permission: 'finance.write', icon: 'ri-file-list-3-line', idempotent: true, idempotencyScope: 'invoices' },
  { actionKey: 'record_payment', label: 'Enregistrer un paiement', description: 'Enregistre un paiement.', resourceType: 'payment', permission: 'finance.write', icon: 'ri-money-dollar-circle-line', idempotent: true, idempotencyScope: 'payments' },
  { actionKey: 'reserve_resource', label: 'Réserver une ressource', description: 'Pose un verrou sur une ressource exclusive.', resourceType: 'resource', permission: 'products.write', icon: 'ri-lock-2-line', idempotent: true, idempotencyScope: 'reservations' },
  { actionKey: 'update_status', label: 'Changer un statut', description: 'Fait évoluer le statut d’une entité.', resourceType: 'entity', permission: 'settings.write', icon: 'ri-shield-check-line', idempotent: false },
];

export function actionMeta(actionKey: string | null | undefined): CoreActionDefinition {
  return CORE_ACTIONS.find((a) => a.actionKey === actionKey) || {
    actionKey: actionKey || 'unknown',
    label: actionKey || 'Action',
    description: 'Action personnalisée.',
    resourceType: 'entity',
    permission: '',
    icon: 'ri-flashlight-line',
    idempotent: false,
  };
}

export interface CoreAction {
  id: number;
  action_id: string | null;
  tenant_id: number | null;
  organization_id: number | null;
  actor_user_id: number | null;
  actor_name: string | null;
  action_key: string;
  resource_type: string | null;
  resource_id: string | null;
  permission: string | null;
  parameters: Record<string, unknown> | null;
  status: string | null;
  result: Record<string, unknown> | null;
  error: string | null;
  idempotency_key: string | null;
  duration_ms: number | null;
  source: string | null;
  created_at: string | null;
}

export interface CoreActionInput<P> {
  actionKey: string;
  tenantId: number | null;
  organizationId: number | null;
  actorUserId?: number | null;
  actorName?: string | null;
  resourceType?: string | null;
  resourceId?: string | number | null;
  permission?: string | null;
  parameters?: P;
  /** Contrôle d'autorisation ; si absent, on ne bloque pas. */
  canAccess?: (permission: string) => boolean;
  idempotencyKey?: string | null;
  idempotencyScope?: string | null;
  source?: string | null;
  /** Exécution métier réelle. Renvoie l'entité produite + le résultat. */
  execute: () => Promise<{ entityType?: string | null; entityId?: string | number | null; result?: unknown }>;
}

export interface CoreActionResult {
  ok: boolean;
  status: ActionStatus;
  actionId: string;
  entityType: string | null;
  entityId: string | null;
  result: unknown;
  error?: string;
  durationMs: number;
  idempotency?: IdempotencyOutcome<unknown>;
}

function newActionId(): string {
  const rand = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 16)
    : Math.random().toString(36).slice(2, 10);
  return `act_${rand}`;
}

async function persistAction(row: Partial<CoreAction>): Promise<void> {
  try {
    await supabase.from('core_actions').insert(row);
  } catch {
    /* la journalisation ne doit jamais bloquer l'action */
  }
}

/**
 * Exécute une action du socle de bout en bout :
 *   permission → (idempotence) → exécution → journalisation.
 * Ne lève jamais : renvoie un résultat normalisé. Les erreurs métier sont
 * capturées et journalisées avec status 'failed'.
 */
export async function runCoreAction<P>(input: CoreActionInput<P>): Promise<CoreActionResult> {
  const started = Date.now();
  const actionId = newActionId();
  const def = actionMeta(input.actionKey);
  const permission = input.permission ?? def.permission ?? null;
  const resourceType = input.resourceType ?? def.resourceType;
  const base = {
    action_id: actionId,
    tenant_id: input.tenantId,
    organization_id: input.organizationId,
    actor_user_id: input.actorUserId ?? null,
    actor_name: input.actorName ?? null,
    action_key: input.actionKey,
    resource_type: resourceType,
    resource_id: input.resourceId != null ? String(input.resourceId) : null,
    permission,
    parameters: (input.parameters ?? null) as Record<string, unknown> | null,
    idempotency_key: input.idempotencyKey ?? null,
    source: input.source || 'core',
  };

  // ── Authorization ──
  if (permission && input.canAccess && !input.canAccess(permission)) {
    const durationMs = Date.now() - started;
    await persistAction({ ...base, status: 'denied', error: `Permission « ${permission} » requise`, duration_ms: durationMs });
    return {
      ok: false,
      status: 'denied',
      actionId,
      entityType: null,
      entityId: null,
      result: null,
      error: `Permission « ${permission} » requise.`,
      durationMs,
    };
  }

  // ── Idempotence (spec 47) ──
  const useIdempotency = Boolean(input.idempotencyKey && input.organizationId);
  try {
    if (useIdempotency) {
      const scope = input.idempotencyScope || def.idempotencyScope || 'general';
      const outcome = await withIdempotency<unknown>(
        {
          tenantId: input.tenantId,
          organizationId: input.organizationId,
          scope,
          key: input.idempotencyKey as string,
          requestPayload: input.parameters ?? null,
        },
        async () => {
          const exec = await input.execute();
          return {
            response: (exec.result ?? null) as unknown,
            entityType: exec.entityType ?? null,
            entityId: exec.entityId ?? null,
          };
        },
      );
      const durationMs = Date.now() - started;
      await persistAction({
        ...base,
        status: outcome.duplicate ? 'duplicate' : 'success',
        result: (outcome.response ?? null) as Record<string, unknown> | null,
        resource_id: outcome.entityId ?? base.resource_id,
        duration_ms: durationMs,
      });
      return {
        ok: true,
        status: outcome.duplicate ? 'duplicate' : 'success',
        actionId,
        entityType: outcome.entityType,
        entityId: outcome.entityId,
        result: outcome.response,
        durationMs,
        idempotency: outcome,
      };
    }

    // ── Exécution simple ──
    const exec = await input.execute();
    const durationMs = Date.now() - started;
    await persistAction({
      ...base,
      status: 'success',
      result: (exec.result ?? null) as Record<string, unknown> | null,
      resource_id: exec.entityId != null ? String(exec.entityId) : base.resource_id,
      duration_ms: durationMs,
    });
    return {
      ok: true,
      status: 'success',
      actionId,
      entityType: exec.entityType ?? resourceType,
      entityId: exec.entityId != null ? String(exec.entityId) : null,
      result: exec.result ?? null,
      durationMs,
    };
  } catch (e) {
    const durationMs = Date.now() - started;
    const message = e instanceof Error ? e.message : "Erreur d'exécution.";
    await persistAction({ ...base, status: 'failed', error: message, duration_ms: durationMs });
    return {
      ok: false,
      status: 'failed',
      actionId,
      entityType: null,
      entityId: null,
      result: null,
      error: message,
      durationMs,
    };
  }
}

export async function listCoreActions(
  organizationId: number,
  filter?: { actionKey?: string; status?: string; limit?: number },
): Promise<CoreAction[]> {
  let query = supabase
    .from('core_actions')
    .select('*')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(filter?.limit ?? 100);
  if (filter?.actionKey) query = query.eq('action_key', filter.actionKey);
  if (filter?.status) query = query.eq('status', filter.status);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreAction[];
}

export interface ActionStats {
  total: number;
  success: number;
  failed: number;
  denied: number;
  duplicate: number;
  byAction: Record<string, number>;
}

export async function fetchActionStats(organizationId: number): Promise<ActionStats> {
  const { data } = await supabase
    .from('core_actions')
    .select('action_key, status')
    .eq('organization_id', organizationId);
  const byAction: Record<string, number> = {};
  const counts = { success: 0, failed: 0, denied: 0, duplicate: 0 };
  (data || []).forEach((r) => {
    const row = r as { action_key: string; status: string | null };
    byAction[row.action_key] = (byAction[row.action_key] || 0) + 1;
    if (row.status === 'success') counts.success += 1;
    else if (row.status === 'failed') counts.failed += 1;
    else if (row.status === 'denied') counts.denied += 1;
    else if (row.status === 'duplicate') counts.duplicate += 1;
  });
  return { total: (data || []).length, ...counts, byAction };
}

export function actionStatusMeta(status: string | null | undefined): { label: string; className: string } {
  switch (status) {
    case 'success': return { label: 'Succès', className: 'bg-primary-100 text-primary-900' };
    case 'duplicate': return { label: 'Rejouée (idempotent)', className: 'bg-secondary-100 text-secondary-900' };
    case 'denied': return { label: 'Refusée', className: 'bg-red-100 text-red-800' };
    case 'failed': return { label: 'Échouée', className: 'bg-red-100 text-red-800' };
    case 'pending': return { label: 'En cours', className: 'bg-accent-100 text-accent-900' };
    default: return { label: status || '—', className: 'bg-background-100 text-foreground-500' };
  }
}